import {RuntimeAdapter} from '@assistant-ui/core/react';
import {AuiProvider, useAui} from '@assistant-ui/store';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import React, {useCallback, useMemo} from 'react';

import {WBAgentRuntime} from '../../agentChat/runtime/backend';
import {AgentChatStoreProvider, useAgentChatStore} from '../../agentChat/store';
import {useAgentChatRuntime} from '../../agentChat/useAgentChatRuntime';
import {useNavContext} from '../NavContextProvider';
import {
  AgentChatAvailabilityContext,
  useComputeAgentChatAvailability,
} from './useAgentChatAvailability';

/**
 * Top-level provider for the Agent Chat feature.
 *
 * When the feature is available (gate + org settings pass), mounts the chat
 * store and AUI runtime. The runtime gracefully handles missing entity/project
 * context (turns are no-ops until context is present).
 */
export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const availability = useComputeAgentChatAvailability();

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      <AgentChatStoreProvider>
        {availability.isAvailable ? (
          <AssistantRuntime>{children}</AssistantRuntime>
        ) : (
          children
        )}
      </AgentChatStoreProvider>
    </AgentChatAvailabilityContext.Provider>
  );
}

function AssistantRuntime({children}: {children: React.ReactNode}) {
  const {params} = useNavContext();
  const wbAgentClient = useWbAgentClient(params.entityName, params.projectName);
  const store = useAgentChatStore();

  const getParentNodeId = useCallback(
    (nodeId: string) => {
      const state = store.getState();
      const thread = state.threads[state.activeThreadId ?? ''];
      return thread?.messageTree.parentId[nodeId] ?? null;
    },
    [store]
  );

  const agentRuntime = useMemo(
    () =>
      wbAgentClient ? new WBAgentRuntime(wbAgentClient, getParentNodeId) : null,
    [wbAgentClient, getParentNodeId]
  );

  const auiRuntime = useAgentChatRuntime(agentRuntime);
  const aui = useAui({threads: RuntimeAdapter(auiRuntime)});

  return <AuiProvider value={aui}>{children}</AuiProvider>;
}
